import {useContext, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment-timezone";
import {DataContext} from "../../../contexts/dataContext";
import {translateService} from "../../../services/TranslateService";
import {
    FuelType,
    Transmission,
    TyreTypes,
    Country,
    FilterParameters,
} from "../Filter";
import styles from "./ActiveFilterIndicators.module.scss";

interface FilterChip {
    key: string;
    label: string;
}

function getActiveFilterChips(params: FilterParameters | undefined): FilterChip[] {
    if (!params) return [];
    const chips: FilterChip[] = [];

    if (params.types && params.types.length > 0) {
        params.types.forEach(t => {
            chips.push({key: `type-${t}`, label: translateService.translate(`vehicleSearch.${t}`)});
        });
    }

    if (params.fuelType && params.fuelType !== FuelType.All) {
        chips.push({key: `fuel-${params.fuelType}`, label: translateService.translate(`vehicleSearch.${params.fuelType}`)});
    }

    if (params.transmission && params.transmission !== Transmission.All) {
        chips.push({key: `trans-${params.transmission}`, label: translateService.translate(`vehicleSearch.${params.transmission}`)});
    }

    if (params.tyreTypesNew && params.tyreTypesNew !== TyreTypes.All) {
        chips.push({key: `tyre-${params.tyreTypesNew}`, label: translateService.translate(`vehicleSearch.${params.tyreTypesNew}`)});
    }

    if (params.selectedTag && params.selectedTag !== Country.All) {
        chips.push({key: `country-${params.selectedTag}`, label: translateService.translate(`vehicleSearch.${params.selectedTag}`)});
    }

    if (params.otherTypes && params.otherTypes.length > 0) {
        params.otherTypes.forEach(o => {
            chips.push({key: `other-${o}`, label: translateService.translate(`vehicleSearch.${o}`)});
        });
    }

    return chips;
}

function getActiveFilterCategoryCount(params: FilterParameters | undefined): number {
    if (!params) return 0;
    let count = 0;
    if (params.types && params.types.length > 0) count++;
    if (params.fuelType && params.fuelType !== FuelType.All) count++;
    if (params.transmission && params.transmission !== Transmission.All) count++;
    if (params.tyreTypesNew && params.tyreTypesNew !== TyreTypes.All) count++;
    if (params.selectedTag && params.selectedTag !== Country.All) count++;
    if (params.otherTypes && params.otherTypes.length > 0) count++;
    return count;
}

function formatSearchDates(startDate: moment.Moment | null, endDate: moment.Moment | null): string {
    if (!startDate) return '';
    if (!endDate || startDate.isSame(endDate, 'day')) {
        return `${startDate.format('D')} ${startDate.format('MMMM')}`;
    }
    return `${startDate.format('D')} - ${endDate.format('D')} ${endDate.format('MMMM')}`;
}

interface IActiveFilterIndicatorsProps {
    setOpenFilters: (open: boolean) => void;
    compact?: boolean;
    loading?: boolean;
}

export const ActiveFilterIndicators = ({setOpenFilters, compact, loading}: IActiveFilterIndicatorsProps) => {
    const {filteredParams, filteredVehicles, searchParameters, rootUrl, sendGa4Event} = useContext(DataContext);
    const [chipsExpanded, setChipsExpanded] = useState(false);

    const chips = useMemo(() => getActiveFilterChips(filteredParams), [filteredParams]);
    const activeCount = useMemo(() => getActiveFilterCategoryCount(filteredParams), [filteredParams]);
    const chipsRef = useRef<HTMLDivElement>(null);
    const [hasNormalOverflow, setHasNormalOverflow] = useState(false);
    const [hiddenChipCount, setHiddenChipCount] = useState(0);

    useEffect(() => {
        const el = chipsRef.current;
        if (!el || compact || chipsExpanded) return;
        const check = () => {
            if (el.children.length === 0) {
                setHasNormalOverflow(false);
                setHiddenChipCount(0);
                return;
            }
            const firstTop = (el.children[0] as HTMLElement).offsetTop;
            let visibleCount = 0;
            for (let i = 0; i < el.children.length; i++) {
                if ((el.children[i] as HTMLElement).offsetTop === firstTop) visibleCount++;
            }
            const wrapsToSecondRow = visibleCount < el.children.length;
            setHasNormalOverflow(wrapsToSecondRow);
            setHiddenChipCount(wrapsToSecondRow ? el.children.length - visibleCount : 0);
        };
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [compact, chipsExpanded, chips]);

    const rawCityName = searchParameters?.searchCity
        || searchParameters?.userPlaceSearch
        || searchParameters?.location?.formatted_address
        || (typeof searchParameters?.location === 'string' ? searchParameters.location : '');
    const cityName = rawCityName?.split(',')[0] || '';
    const dateLabel = formatSearchDates(
        searchParameters?.startDate ?? null,
        searchParameters?.endDate ?? null
    );

    const openFilters = () => {
        setOpenFilters(true);
        sendGa4Event('showAdvancedOptions');
    };

    const hasCompactOverflow = compact && chips.length > 2;
    const visibleChips = hasCompactOverflow && !chipsExpanded ? chips.slice(0, 2) : chips;
    const showExpandToggle = hasCompactOverflow || (!compact && hasNormalOverflow);

    const filterIcon = (
        <div className={compact ? styles.compactFilterIcon : styles.activeFilterIconWrapper} onClick={openFilters}>
            <img
                width="35"
                height="35"
                className={styles.activeFilterIcon}
                src={rootUrl + "/assets/icons/new/search/filters-orange.svg"}
                alt={translateService.translate('vehicleSearch.filterResults')}
                loading="lazy"
            />
            {activeCount > 0 && (
                <span className={styles.activeFilterBadge}>{activeCount}</span>
            )}
        </div>
    );

    const chipButtons = visibleChips.map(chip => (
        <button
            key={chip.key}
            className={styles.activeFilterChip}
            onClick={openFilters}
        >
            {chip.label}
            <span className={styles.chipClose}>&times;</span>
        </button>
    ));

    return (
        <div className={`${styles.activeFilterContainer} ${compact ? styles.activeFilterContainerCompact : ''} ${compact && chips.length === 0 ? styles.compactNoFilters : ''}`}>
            {compact ? (
                <div className={styles.compactFilterRow}>
                    {chipButtons}
                    {filterIcon}
                </div>
            ) : (
                <>
                    <div className={styles.activeFilterTopRow}>
                        <button className={styles.searchSummaryButton} onClick={openFilters}>
                            <span className={styles.searchSummaryLine1}>
                                {translateService.translate('vehicleSearch.carsOnSite')} {cityName}
                            </span>
                            {dateLabel && (
                                <span className={styles.searchSummaryLine2}>
                                    {dateLabel}
                                </span>
                            )}
                        </button>
                        {filterIcon}
                    </div>
                    {visibleChips.length > 0 && (
                        <div
                            ref={chipsRef}
                            className={`${styles.activeFilterChips} ${!chipsExpanded ? styles.activeFilterChipsCollapsed : ''}`}
                        >
                            {chipButtons}
                        </div>
                    )}
                </>
            )}
            {showExpandToggle && (() => {
                const extraCount = compact ? chips.length - visibleChips.length : hiddenChipCount;
                return (
                    <button
                        className={styles.drawerToggle}
                        onClick={() => setChipsExpanded(prev => !prev)}
                    >
                        {chipsExpanded ? '⇑' : '⇓'}
                        {!chipsExpanded && extraCount > 0 && (
                            <span className={styles.hiddenCount}>+{extraCount}</span>
                        )}
                    </button>
                );
            })()}
            {compact && !loading && (!filteredVehicles || filteredVehicles.length === 0) && chips.length > 0 && (
                <p className={styles.noResultsText}>
                    {translateService.translate('vehicleSearch.noVehiclesForFilters')}
                </p>
            )}
        </div>
    );
};
